import type { PrivacyNotice } from './types';

export const PRIVACY_NOTICE_STUDENTS: PrivacyNotice = {
  id: 'student',
  label: 'For students',
  // No source-level title — the student .docx opens directly with the
  // intro paragraph, so we don't fabricate one.
  title: '',
  intro: [
    'Hi! This page tells you what information Norhi keeps about you, why we keep it, and how we look after it.',
    'If you have any questions, ask your teacher or a parent.',
  ],
  lastUpdated: '18 September 2026',
  version: '1.2',
  blocks: [
    { kind: 'heading', level: 2, text: 'Who is Norhi?' },
    {
      kind: 'paragraph',
      text: 'Norhi is a company that runs enrichment programmes in schools. If you are taking part in EduCity, you are using our programme. We are based in London and we take looking after your information very seriously.',
    },

    { kind: 'heading', level: 2, text: 'What information do we have about you?' },
    {
      kind: 'paragraph',
      text: 'Your school gave us a small amount of information so we could set up your account. Here is what we have:',
    },
    {
      kind: 'bullets',
      items: [
        'Your first name and last name',
        'Your school email address',
        'Your year group',
      ],
    },
    {
      kind: 'paragraph',
      text: 'That is it. We do not have your home address, phone number, date of birth, or any photos of you.',
    },
    {
      kind: 'paragraph',
      text: 'We will never ask you for your password. If anyone ever asks for your password, tell your teacher straight away.',
    },

    { kind: 'heading', level: 2, text: 'How do you log in?' },
    {
      kind: 'paragraph',
      text: 'You log in using your school Google account — the same one you might use for other school things. When you log in, Google checks that it is really you. Norhi never sees your password. Google is a separate company that helps keep your login secure.',
    },

    { kind: 'heading', level: 2, text: 'Your questions, answered' },

    { kind: 'heading', level: 3, text: 'Why does Norhi need my name and email?' },
    {
      kind: 'paragraph',
      text: 'We need your name to set up your account on the platform so your teacher can see how you are getting on. We need your email so you can log in using your school Google account.',
    },

    { kind: 'heading', level: 3, text: 'Who can see my information?' },
    {
      kind: 'paragraph',
      text: 'Your teacher can see your name and how you are doing in the programme. Other students in your class can see your first name on the class leaderboard — but nobody outside your class can see it. Norhi staff can also see your account details to keep the platform running.',
    },

    { kind: 'heading', level: 3, text: 'Can my classmates see my score?' },
    {
      kind: 'paragraph',
      text: 'Yes — the leaderboard shows your first name and your position. Only students in your class can see this. Nobody from another school or outside your class can see your score.',
    },

    { kind: 'heading', level: 3, text: 'Will Norhi contact my parents?' },
    {
      kind: 'paragraph',
      text: 'At the end of the programme, you can choose to share a progress report with a parent or guardian by typing their email address into the app. This is completely optional — you do not have to do it. If you do, we only use that email address to send the report, and we delete it shortly afterwards.',
    },

    { kind: 'heading', level: 3, text: 'Does Norhi show adverts?' },
    {
      kind: 'paragraph',
      text: 'No. We do not show adverts on our platform and we will never use your information for advertising.',
    },

    { kind: 'heading', level: 3, text: 'How long does Norhi keep my information?' },
    {
      kind: 'paragraph',
      text: 'We keep your account information for up to 8 years after you last use a Norhi programme. This is so that if you do another Norhi programme at secondary school, we can set up your account easily. However, if your parent or guardian would like us to delete your information sooner, they can ask us to do that at any time by emailing enquiries@norhi.io.',
    },

    { kind: 'heading', level: 3, text: 'Is my information safe?' },
    {
      kind: 'paragraph',
      text: 'Yes. Your information is stored on secure servers in Europe. Everything is encrypted, which means it is scrambled so that only authorised people can read it. We use a company called Amazon Web Services (AWS) to store data safely.',
    },

    { kind: 'heading', level: 3, text: 'Can I get my information deleted?' },
    {
      kind: 'paragraph',
      text: 'Yes. You — or a parent or guardian — can ask us to delete your information at any time. Just email us at enquiries@norhi.io or ask your teacher to get in touch with us.',
    },

    { kind: 'heading', level: 2, text: 'What are my rights?' },
    {
      kind: 'paragraph',
      text: 'Even as a young person, you have rights over your personal information. Here is what you can do:',
    },
    {
      kind: 'bullets',
      items: [
        'Ask to see the information we hold about you',
        'Ask us to correct anything that is wrong',
        'Ask us to delete your information',
        'Ask a parent or guardian to help you with any of the above',
      ],
    },
    {
      kind: 'paragraph',
      text: 'To do any of these things, email us at enquiries@norhi.io, or ask your teacher to contact us on your behalf.',
    },

    { kind: 'heading', level: 2, text: 'Staying safe online' },
    {
      kind: 'paragraph',
      text: 'Here are a few simple tips to help keep your information safe while using Norhi:',
    },
    {
      kind: 'bullets',
      items: [
        'Never share your password with anyone — not even a friend',
        'Always log out when you have finished, especially on a shared computer',
        'If something does not look right on the platform, tell your teacher straight away',
        'If you think someone else has accessed your account, tell your teacher straight away',
      ],
    },

    { kind: 'heading', level: 2, text: 'The full Privacy Notice' },
    {
      kind: 'paragraph',
      text: 'This is a summary to help you understand how Norhi looks after your information. The full, legal Privacy Notice — which contains all the details — is available at www.norhi.io. You or a parent can read it there at any time.',
    },

    { kind: 'heading', level: 2, text: 'Questions?' },
    {
      kind: 'paragraph',
      text: 'If you have any questions about your privacy or your information, you can:',
    },
    {
      kind: 'bullets',
      items: [
        'Ask your class teacher',
        'Ask a parent or guardian',
        'Email us at enquiries@norhi.io',
      ],
    },
  ],
};
